'use client'
import React, { Fragment, memo, useCallback, useState, useEffect } from 'react'
import Link from 'next/link'
import { SubmitHandler, useForm } from 'react-hook-form'
import { SectionNoneW } from '../../parts/section-none-w'
import { Section440 } from '../../parts/section-440'
import useScrollObserve from '../../../hooks/useScrollObserve'
import { Box } from '../../parts/box'
import { Button } from '../../parts/button'
import { Contact } from '../../parts/contact'
import { Figure } from '../../parts/figure'
import { Flex } from '../../parts/flex'
import { Heading } from '../../parts/heading'
import { Select } from '@/components/forms/ui/select'
import { FieldBlock } from '@/components/forms/ui/field-block'
import { Input } from '@/components/forms/ui/input'
import {
  email,
  literal,
  minLength,
  object,
  optional,
  Output,
  regex,
  string
} from 'valibot'
import { useSendApi } from '@/components/forms/hook'
import { useSelector } from 'react-redux'
import { selectUrlState } from '@/components/utils/appSlice'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { Textarea } from '@/components/forms/ui/textarea'
import { Checkbox } from '@/components/parts/checkbox'
import { ValidationError } from '@/components/forms/ui/validation-error'
import { Lead } from '@/components/parts/lead'
import { PaginatedServices } from '@/types/newt/service'
import { Department } from '@/types/newt/department'
import { useThanksForAppRouter } from '@/hooks/useThanksForAppRouter'
import { Opportunity } from '@/types/newt/opportunity'
import { ColumnM60Service } from '@/components/parts/column-m60-service'

type ContactFormProps = {
  departments: Department[]
  opportunities: Opportunity[]
}

const lawgueRequestSchema = object({
  salesforceId: string(),
  company: string([minLength(1, '貴社名/貴所名をご入力ください')]),
  lastname: string([minLength(1, '必須項目です')]),
  firstname: string([minLength(1, '必須項目です')]),
  email: string([
    minLength(1, '必須項目です'),
    email('入力されたメールアドレスに間違いがあります')
  ]),
  phone_number: string([
    minLength(1, '必須項目です'),
    regex(/^\d{2,4}-\d{1,4}-\d{4}$/, '電話番号の形式を確認してください')
  ]),
  select_form1: string([minLength(1, '必須項目です')]),
  department: optional(string()),
  select_form2: string([minLength(1, '必須項目です')]),
  select_form3: string([minLength(1, '必須項目です')]),
  textarea: optional(string()),
  checkbox: literal(true, 'チェックを入れてください'),
  first_view_route: optional(string()),
  last_view_route: optional(string())
})

export type LawgueRequestSchema = Output<typeof lawgueRequestSchema>

const ContactForm: React.FC<ContactFormProps> = ({
  departments,
  opportunities
}) => {
  const { sendApi, isSendError } = useSendApi(
    '/lawgue-request',
    '/thanks_download/'
  )
  const [isButtonClicked, setIsButtonClicked] = useState(false)
  const urlSelector = useSelector(selectUrlState)
  const { ObserveState } = useScrollObserve()
  const [firstViewRoute, setFirstViewRoute] = useState('')
  const [lastViewRoute, setLastViewRoute] = useState('')
  const { pushThanks } = useThanksForAppRouter()
  const [localError, setLocalError] = useState(false)

  useEffect(() => {
    const fullPath = `${window.location.pathname}${window.location.search}`

    if (!sessionStorage.getItem('firstViewURL')) {
      sessionStorage.setItem('firstViewURL', fullPath)
    }

    setFirstViewRoute(sessionStorage.getItem('firstViewURL') || '/')
    setLastViewRoute(fullPath)
  }, [])

  const handleOnSubmit: SubmitHandler<LawgueRequestSchema> = useCallback(
    async (data) => {
      const params = new URLSearchParams(window.location.search)
      const formData = {
        ...data,
        first_view_route: firstViewRoute,
        last_view_route: lastViewRoute,
        utm_params: Object.fromEntries(params)
      }
      // console.log('Submitting form data:', formData)
      setIsButtonClicked(true)
      setLocalError(false)
      try {
        await sendApi(JSON.stringify(formData))
        pushThanks('thanks_download')
      } catch (error) {
        console.error('Sending API request failed:', error)
        setLocalError(true)
      } finally {
        setIsButtonClicked(false)
      }
    },
    [sendApi, urlSelector, firstViewRoute, lastViewRoute, pushThanks]
  )

  const {
    register,
    handleSubmit,
    formState: { errors: formatError, isLoading }
  } = useForm<Output<typeof lawgueRequestSchema>>({
    resolver: valibotResolver(lawgueRequestSchema)
  })

  return (
    <Section440 padding="mt-20 lg:pb-20 pt-10 pb-10 px-8">
      <Heading
        Tag="h2"
        size="text-2xl"
        spsize="text-lg lg:text-2xl"
        textColor="textblack"
        textalign="text-center"
      >
        ダウンロードはこちら
      </Heading>
      <form
        className="lg:mt-10 mt-6 lg:pb-10"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <fieldset>
          <div className="mx-auto w-full">
            <input type="hidden" name="first_view_route" defaultValue="/" />
            <input
              type="hidden"
              name="last_view_route"
              defaultValue="/contact"
            />
            <input
              type="hidden"
              value="/l/939003/2022-02-23/bp1m"
              {...register('salesforceId')}
            />
            <div className="relative mb-6 done_input">
              <FieldBlock
                labelText={'貴社名／貴所名'}
                htmlFor={'company'}
                required
                error={formatError.company}
              >
                <Input
                  id={'company'}
                  type="text"
                  placeholder="ローグ株式会社"
                  autoComplete="company"
                  register={register('company')}
                />
              </FieldBlock>
            </div>
            <div className="relative mb-6 done_input">
              <div className="lg:flex">
                <div className="lg:first-of-type:mr-6 lg:first-of-type:mb-0 first-of-type:mb-6 relative lg:w-[50%] done_input">
                  <FieldBlock
                    labelText={'姓'}
                    htmlFor={'lastname'}
                    required
                    error={formatError.lastname}
                  >
                    <Input
                      id={'lastname'}
                      type="text"
                      placeholder="山田"
                      autoComplete="family-name"
                      register={register('lastname')}
                    />
                  </FieldBlock>
                </div>
                <div className="lg:first-of-type:mr-6 lg:first-of-type:mb-0 first-of-type:mb-6 relative lg:w-[50%] done_input">
                  <FieldBlock
                    labelText={'名'}
                    htmlFor={'firstname'}
                    required
                    error={formatError.firstname}
                  >
                    <Input
                      id={'firstname'}
                      type="text"
                      placeholder="太郎"
                      autoComplete="given-name"
                      register={register('firstname')}
                    />
                  </FieldBlock>
                </div>
              </div>
            </div>
            <div className="relative my-6 done_input">
              <FieldBlock
                labelText={'メールアドレス'}
                htmlFor={'email'}
                required
                error={formatError.email}
              >
                <Input
                  id={'email'}
                  type="email"
                  placeholder="lawgue@mail.com"
                  autoComplete="email"
                  register={register('email')}
                />
              </FieldBlock>
            </div>
            <div className="relative my-6 done_input">
              <FieldBlock
                labelText={'電話番号（ハイフンあり）'}
                htmlFor={'phone_number'}
                required
                error={formatError.phone_number}
              >
                <Input
                  id={'phone_number'}
                  type="text"
                  placeholder="080-0000-0000"
                  autoComplete="phone_number"
                  register={register('phone_number')}
                />
              </FieldBlock>
            </div>
            <div className="relative my-6 done_input">
              <FieldBlock
                labelText={'部署区分'}
                htmlFor={'select_form1'}
                required
                error={formatError.select_form1}
              >
                <Select
                  id={'select_form1'}
                  register={register('select_form1')}
                  options={[
                    { label: '選択してください。', value: '' },
                    ...departments.map((dept) => ({
                      label: dept.title,
                      value: dept.title
                    }))
                  ]}
                />
              </FieldBlock>
            </div>
            <div className="relative my-6 done_input">
              <FieldBlock labelText={'部署名'} htmlFor={'department'}>
                <Input
                  id={'department'}
                  type="text"
                  placeholder="法務部"
                  autoComplete="department"
                  register={register('department')}
                />
              </FieldBlock>
            </div>
            <div className="relative my-6 done_input">
              <FieldBlock
                labelText={'従業員数'}
                htmlFor={'select_form2'}
                required
                error={formatError.select_form2}
              >
                <Select
                  id={'select_form2'}
                  register={register('select_form2')}
                  options={[
                    {
                      label: '選択してください。',
                      value: ''
                    },
                    {
                      label: '1～10人',
                      value: '1～10人'
                    },
                    {
                      label: '11～50人',
                      value: '11～50人'
                    },
                    {
                      label: '51～100人',
                      value: '51～100人'
                    },
                    {
                      label: '101～300人',
                      value: '101～300人'
                    },
                    {
                      label: '301～500人',
                      value: '301～500人'
                    },
                    {
                      label: '501～1,000人',
                      value: '501～1,000人'
                    },
                    {
                      label: '1,001～3,000人',
                      value: '1,001～3,000人'
                    },
                    {
                      label: '3,001～5,000人',
                      value: '3,001～5,000人'
                    },
                    {
                      label: '5,001～10,000人',
                      value: '5,001～10,000人'
                    },
                    {
                      label: '10,001人以上',
                      value: '10,001人以上'
                    }
                  ]}
                />
              </FieldBlock>
            </div>
            <div className="relative mt-6 done_input">
              <FieldBlock
                labelText={'LAWGUEを知ったきっかけ'}
                htmlFor={'select_form3'}
                required
                error={formatError.select_form3}
              >
                <Select
                  id={'select_form3'}
                  register={register('select_form3')}
                  options={opportunities.map((opt) => ({
                    label: opt.label,
                    value: opt.value
                  }))}
                />
              </FieldBlock>
            </div>
          </div>
          <div className="relative mt-6">
            <FieldBlock
              labelText={'具体的なお悩みがあればご記入ください'}
              htmlFor={'textarea'}
            >
              <Textarea
                id={'textarea'}
                placeholder="例）文書作成業務の効率化に困っている"
                autoComplete="textarea"
                register={register('textarea')}
              />
            </FieldBlock>
          </div>
          <div className="relative my-6">
            <Checkbox
              htmlfor="checkbox"
              required
              require={true}
              register={register}
            />
            <ValidationError error={formatError.checkbox} />
          </div>
          <Lead
            textalign="text-left"
            spsize="text-xs lg:text-base"
            size="text-xs"
            lead="同業他社および当社の競合企業にあたる企業の方の資料請求はご遠慮させていただきます。何卒ご了承ください。"
            Tag="p"
            margin="mt-6"
          />
        </fieldset>
        <div className="text-center mt-14">
          <Button
            size="medium"
            bg="visible"
            isLoading={isButtonClicked || isLoading}
            label={isButtonClicked ? '送信中' : '送信する'}
            type="submit"
          />
        </div>
      </form>
      {(isSendError || localError) && (
        <div className="py-4 px-6 mt-10 bg-[#FF413614] border-[#FF4136] border-solid border-[1px] rounded-[4px] text-left">
          <p className="text-base text-[#FF4136]">
            エラーが起こったため送信することができませんでした
          </p>
          <p className="text-base mt-6">
            お手数ですがもう一度ご送信ください。
            <br />
            それでも送信できない場合は、すこし時間を置いてお試しくださいませ。
          </p>
        </div>
      )}
    </Section440>
  )
}

type RequestDetailTemplateProps = {
  relatedService: PaginatedServices | null
  departments: Department[]
  opportunities: Opportunity[]
}

export const RequestDetailTemplate = memo(
  ({
    relatedService,
    departments,
    opportunities
  }: RequestDetailTemplateProps) => {
    return (
      <>
        <div className="relative">
          <div className="max-w-[1200px] mx-auto lg:grid lg:grid-cols-[minmax(auto,716px)_420px] lg:gap-x-10 lg:px-6 xl:px-0">
            <Box customstyle="simple box relative bg-[transparent] undefined lg:max-w-[716px] w-full px-6 lg:pl-0 mr-16 undefined max-w-full">
              <SectionNoneW padding="pt-8 pb-16 lg:py-16">
                <Heading
                  Tag="h1"
                  size="text-3xl"
                  spsize="text-lg lg:text-3xl"
                  textColor="textblack"
                  textalign="text-left"
                >
                  {`3分でわかるLAWGUE製品資料`}
                </Heading>
                <div className="service_detail mt-6 lg:mt-10 overflow-hidden">
                  <figure className="mb-4 lg:mb-10">
                    <img
                      src="/images/24e4bbfb517c88ed6b2eec3b1f573909.webp"
                      alt=""
                      className="w-full rounded-lg"
                    />
                  </figure>
                  <div>
                    <p>
                      LAWGUEのことを3分で手軽に理解できる資料です。
                      <br />
                      「LAWGUEってなに？」「なにが出来るの？」「どんな導入効果があるの？」など、初めての方にもLAWGUEをわかりやすく説明した資料です
                    </p>
                  </div>
                  <div className="indexContents lg:mt-10 mt-8">
                    <p>目次</p>
                    <div>FRAIMのご紹介</div>
                    <div>文書関連業務における課題</div>
                    <div>LAWGUEの提供価値</div>
                    <div>ご導入に関するご案内</div>
                    <div>LAWGUEの機能紹介</div>
                  </div>
                  <div className="lg:mt-10 mt-8">
                    <figure className="size-full">
                      <img
                        decoding="async"
                        width="621"
                        height="350"
                        src="/images/c314bf2cffa35b4e0cb09a52dd464017.webp"
                        alt=""
                        className="max-w-full mt-2"
                        srcSet="/images/c314bf2cffa35b4e0cb09a52dd464017.webp 621w, /images/c314bf2cffa35b4e0cb09a52dd464017-300x169.webp 300w, /images/c314bf2cffa35b4e0cb09a52dd464017-304x171.webp 304w, /images/c314bf2cffa35b4e0cb09a52dd464017-282x159.webp 282w"
                        sizes="(max-width: 621px) 100vw, 621px"
                      />
                      <figcaption>ダウンロード資料のイメージ</figcaption>
                    </figure>
                    <p>
                      LAWGUEを活用して文書作成を効率化したい！
                      <br />
                      という方は、ぜひご一読ください。
                    </p>
                  </div>
                </div>
              </SectionNoneW>
              <SectionNoneW customstyle="!px-0">
                <Heading
                  Tag="h2"
                  size="text-2xl"
                  spsize="text-base lg:text-2xl"
                  textColor="textblack"
                  textalign="text-center"
                >
                  そのほかのお役立ち資料
                </Heading>
                <ColumnM60Service>
                  {relatedService &&
                    relatedService.items &&
                    relatedService.items.map((val, i) => {
                      if (i >= 2) return null
                      return (
                        <Fragment key={i}>
                          <Box
                            customstyle="w-full lg:max-w-[calc(50%-1rem)] xl:max-w-[calc(50%-1rem)]"
                            thumnail={false}
                          >
                            <Link
                              href={`/service/${val.slug}`}
                              className="block thumbnail_hover"
                            >
                              <Flex flex="flex flex-col-reverse">
                                <div>
                                  <div className="mt-3">
                                    <Heading
                                      Tag="h3"
                                      size="text-base"
                                      textalign="text-center"
                                    >
                                      {val.title}
                                    </Heading>
                                  </div>
                                </div>
                                <Figure
                                  thumnail={true}
                                  images={val.eyecatch.src}
                                  active="thumnail_etc rounded-lg"
                                />
                              </Flex>
                            </Link>
                          </Box>
                        </Fragment>
                      )
                    })}
                </ColumnM60Service>
              </SectionNoneW>
            </Box>
            <ContactForm
              departments={departments}
              opportunities={opportunities}
            />
          </div>
        </div>
        <Contact bg="bg-lawguebase" />
      </>
    )
  }
)

RequestDetailTemplate.displayName = 'RequestDetailTemplate'
